<script lang="js">
import axios from "axios";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import "jspdf-autotable";

export default {
    name: "MyCoursesCard",
    props: {
        height: {
            type: String,
            default: "500px",
        },
    },
    data() {
        return {
            logger: null,
            courseRegistration: null,
            programDetails: { minUnits: 0, maxUnits: 0 },
            currentSession: null,
            courses: [],
            totalUnits: 0,
            enabled: false,
            statusReason: "",
            requiresPayment: false,
            requiresAdmission: false,
            loading: true,
            dataLoading: false,
            selectedSemester: null,
            yearSemesters: [], // Will be dynamically populated from program structure
            availableCourses: [],
            selectedCourseIds: [],
        };
    },
    computed: {
        isAuthenticated() {
            return this.$store.getters.isAuthenticated;
        },
        currentUser() {
            const user = this.$store.getters.getUser || {};
            const student = this.$store.getters.getStudent || {};
            const enrollment = student.currentEnrollmentId || {};
            return {
                ...user,
                ...student,
                firstname: user.firstName,
                othername: user.otherName,
                lastname: user.lastName,
                matricNumber: student.matriculationNumber,
                year: enrollment.year,
                semester: enrollment.semester,
                program: enrollment.programOfferingId || student.initialProgramOfferingId,
            };
        },
        // Check if selected semester matches student's current year and semester
        isCurrentSemester() {
            if (!this.currentUser || !this.selectedSemester) return false;
            return this.selectedSemester.year === this.currentUser.year &&
                   this.selectedSemester.semester === this.currentUser.semester;
        },
        isDraftOrNotRegistered() {
            return !this.courseRegistration || this.courseRegistration.status === "draft";
        },
        // Check if student has any registration (even temporary/not started)
        hasStartedRegistration() {
            // If there's a registration object that's not temporary, or has courses, student has started
            return this.courseRegistration && (!this.courseRegistration._isTemporary || this.courses.length > 0);
        },
        canSubmit() {
            return this.courses.length > 0 && this.totalUnits >= this.programDetails.minUnits;
        },
        hasReachedMaxUnits() {
            return this.totalUnits >= this.programDetails.maxUnits;
        },
        // Check if student can print - only for submitted/approved registrations in current semester
        canPrint() {
            return (
                this.courseRegistration &&
                (this.courseRegistration.status === "submitted" ||
                 this.courseRegistration.status === "approved") &&
                this.isCurrentSemester &&
                this.courses.length > 0
            );
        },
        // Check if we should show action buttons (Add/Submit) or Print button
        showActionButtons() {
            return this.isDraftOrNotRegistered && this.isCurrentSemester && this.isAdmittedStudent;
        },
        // Check if student is admitted (has active status and matric number)
        isAdmittedStudent() {
            if (!this.currentUser) return false;
            return this.$store.getters.getStudent?.status === "active" && !!this.currentUser.matricNumber;
        },
        statusBadgeClass() {
            // If no registration or temporary (not saved in DB), show "Not Started" style
            if (!this.courseRegistration || this.courseRegistration._isTemporary) {
                return "bg-secondary";
            }

            switch (this.courseRegistration.status) {
                case "draft": return "bg-warning";
                case "submitted": return "bg-primary";
                case "approved": return "bg-success";
                case "rejected": return "bg-danger";
                default: return "bg-secondary";
            }
        },
        statusText() {
            // If no registration or temporary (not saved in DB), show "Not Started"
            if (!this.courseRegistration || this.courseRegistration._isTemporary) {
                return "Not Started";
            }

            switch (this.courseRegistration.status) {
                case "draft": return "Draft";
                case "submitted": return "Submitted";
                case "approved": return "Approved";
                case "rejected": return "Rejected";
                default: return "Unknown";
            }
        },
    },
    created() {
        // Create a logger instance for this component
        this.logger = this.$createLogger('MyCoursesCard');
    },
    async mounted() {
        this.logger.info("MyCoursesCard mounted", {
            authChecked: this.$store.state.authChecked,
            isAuthenticated: this.isAuthenticated,
            user: this.$store.getters.getUser ? 'exists' : 'null',
            apiBaseUrl: process.env.VUE_APP_API_BASE_URL
        });

        // Wait for auth to complete if needed
        if (!this.$store.state.authChecked) {
            this.logger.info("Waiting for auth check to complete...");
            await new Promise((resolve) => {
                const unwatch = this.$store.watch(
                    (state) => state.authChecked,
                    (authChecked) => {
                        this.logger.info("Auth check completed:", authChecked);
                        if (authChecked) {
                            unwatch();
                            resolve();
                        }
                    }
                );

                // Timeout after 10 seconds
                setTimeout(() => {
                    this.logger.warn("Auth check timeout");
                    unwatch();
                    resolve();
                }, 10000);
            });
        }

        // Now try to check status
        this.logger.info("Starting course registration status check...");
        await this.checkCourseRegistrationStatus();

        // Always load program structure and course registration
        // This allows students to see their existing registrations even when closed
        await this.loadProgramStructure();
        await this.loadCourseRegistration();
    },
    methods: {
        async checkCourseRegistrationStatus() {
            try {
                this.loading = true;

                // Debug: Log the URL being called and configuration
                const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/course-registration/status`;
                this.logger.info("=== API Call Debug ===", {
                    url: apiUrl,
                    baseUrl: process.env.VUE_APP_API_BASE_URL,
                    timestamp: new Date().toISOString(),
                    userAgent: navigator.userAgent,
                    axios_defaults: {
                        baseURL: axios.defaults.baseURL,
                        timeout: axios.defaults.timeout,
                        withCredentials: axios.defaults.withCredentials
                    }
                });

                // Test direct fetch first to see if axios is the issue
                try {
                    const fetchResponse = await fetch(apiUrl, {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    this.logger.info("Fetch Response:", {
                        status: fetchResponse.status,
                        statusText: fetchResponse.statusText,
                        ok: fetchResponse.ok,
                        type: fetchResponse.type,
                        url: fetchResponse.url,
                        headers: Array.from(fetchResponse.headers.entries())
                    });
                } catch (fetchError) {
                    this.logger.error("Fetch Error:", fetchError);
                }

                const response = await axios.get(apiUrl, {
                    withCredentials: true,
                    timeout: 10000, // 10 second timeout
                });

                this.logger.info("Axios Response received:", response.status, response.data);

                if (response.data.success) {
                    this.enabled = response.data.data?.enabled;
                    this.statusReason = response.data.data?.reason;
                    this.requiresPayment = response.data.requiresPayment || false;
                    this.requiresAdmission = response.data.requiresAdmission || false;

                    this.logger.info("Course registration status:", {
                        enabled: this.enabled,
                        reason: this.statusReason,
                        requiresPayment: this.requiresPayment,
                        requiresAdmission: this.requiresAdmission
                    });
                } else {
                    this.enabled = false;
                    this.statusReason = response.data.reason || "Course registration not available";
                    this.requiresAdmission = response.data.requiresAdmission || false;
                }
            } catch (error) {
                this.logger.error("=== Axios Error Debug ===", {
                    message: error.message,
                    name: error.name,
                    stack: error.stack?.split('\n').slice(0, 3),
                    response: error.response ? {
                        status: error.response.status,
                        statusText: error.response.statusText,
                        data: error.response.data,
                        headers: error.response.headers
                    } : 'No response',
                    request: error.request ? {
                        method: error.config?.method,
                        url: error.config?.url,
                        withCredentials: error.config?.withCredentials,
                        timeout: error.config?.timeout,
                        headers: error.config?.headers
                    } : 'No request',
                    code: error.code,
                    isAxiosError: error.isAxiosError
                });

                this.enabled = false;

                if (error.response?.status === 401) {
                    this.statusReason = "Authentication required - Please log in";
                } else if (error.response?.status === 404) {
                    this.statusReason = "Course registration service not found";
                } else if (error.response?.status === 403) {
                    this.statusReason = "Access denied - Student account required";
                } else {
                    this.statusReason = `Unable to verify course registration status (${error.response?.status || 'Network Error'})`;
                }
            } finally {
                this.loading = false;
            }
        },

        async loadProgramStructure() {
            if (!this.isAuthenticated) {
                return;
            }

            try {
                // Make a call to get program structure without year/semester params
                const response = await axios.get(
                    `${process.env.VUE_APP_API_BASE_URL}/course-registration`
                );

                if (response.data.success && response.data.data.programStructure) {
                    // Build year/semester options from program structure
                    this.yearSemesters = [];
                    const years = response.data.data.programStructure.years;
                    if (years && years.length > 0) {
                        years.forEach(yearObj => {
                            if (yearObj.semesters && yearObj.semesters.length > 0) {
                                yearObj.semesters.forEach(semester => {
                                    this.yearSemesters.push({
                                        value: { year: yearObj.year, semester: semester },
                                        text: `Year ${yearObj.year} Semester ${semester}`
                                    });
                                });
                            }
                        });
                    }

                    this.logger.info("Program structure loaded:", {
                        availableYearSemesters: this.yearSemesters.length
                    });
                }
            } catch (error) {
                this.logger.error("Error loading program structure:", error);
                // Fallback to default structure if API fails
                this.yearSemesters = [
                    { value: { year: 1, semester: 1 }, text: "Year 1 Semester 1" },
                    { value: { year: 1, semester: 2 }, text: "Year 1 Semester 2" },
                    { value: { year: 2, semester: 1 }, text: "Year 2 Semester 1" },
                    { value: { year: 2, semester: 2 }, text: "Year 2 Semester 2" },
                ];
            }
        },

        async loadCourseRegistration(year = null, semester = null) {
            if (!this.isAuthenticated) {
                return;
            }

            try {
                this.dataLoading = true;
                const params = {};
                if (year) params.year = year;
                if (semester) params.semester = semester;

                const response = await axios.get(
                    `${process.env.VUE_APP_API_BASE_URL}/course-registration`,
                    { params }
                );

                if (response.data.success) {
                    this.courseRegistration = response.data.data.registration;
                    this.programDetails = response.data.data.programDetails;
                    this.currentSession = response.data.data.currentSession;
                    this.courses = this.courseRegistration.courses || [];
                    this.totalUnits = this.courseRegistration.totalUnits || 0;

                    // Set selected semester if not already set
                    if (!this.selectedSemester && this.courseRegistration) {
                        const semesterOption = this.yearSemesters.find(
                            opt => opt.value.year === this.courseRegistration.year &&
                                opt.value.semester === this.courseRegistration.semester
                        );
                        if (semesterOption) {
                            this.selectedSemester = semesterOption.value;
                        }
                    }

                    this.logger.info("Course registration loaded:", {
                        courses: this.courses.length,
                        totalUnits: this.totalUnits,
                        status: this.courseRegistration.status
                    });
                }
            } catch (error) {
                this.logger.error("Error loading course registration:", error);
            } finally {
                this.dataLoading = false;
            }
        },

        async onSemesterChange() {
            if (this.selectedSemester) {
                await this.loadCourseRegistration(
                    this.selectedSemester.year,
                    this.selectedSemester.semester
                );
            }
        },

        async loadAvailableCourses() {
            if (!this.isAuthenticated) {
                return;
            }

            try {
                const params = {};
                if (this.selectedSemester) {
                    params.year = this.selectedSemester.year;
                    params.semester = this.selectedSemester.semester;
                }

                const response = await axios.get(
                    `${process.env.VUE_APP_API_BASE_URL}/course-registration/available-courses`,
                    { params }
                );

                if (response.data.success) {
                    this.availableCourses = response.data.data;
                }
            } catch (error) {
                this.logger.error("Error loading available courses:", error);
                this.availableCourses = [];
            }
        },

        async addCourse() {
            // Load available courses first
            await this.loadAvailableCourses();

            if (this.availableCourses.length === 0) {
                Swal.fire({
                    icon: "info",
                    title: "No Courses Available",
                    text: "There are no courses available for registration at this time.",
                });
                return;
            }

            // Create course selection modal
            const coursesHtml = this.availableCourses
                .map((course) => {
                    // const lecturerName = course.lecturer
                    //     : "TBA";

                    return `
            <div class="form-check mb-3 p-3 border rounded">
              <input class="form-check-input" type="checkbox" value="${course._id}" id="course-${course._id}">
              <label class="form-check-label w-100" for="course-${course._id}">
                <div class="d-flex justify-content-between align-items-start">
                  <div class="text-start">
                    <h6 class="mb-1 fw-bold">Course Code: <span class="text-uppercase">${course.course.code} </span></h6>
                    <p class="mb-1">Course Title: ${course.course.title}</p>
                  </div>
                  <div class="text-end">
                    <span class="badge bg-primary">${course.units} Units</span>
                    <br>
                    <small class="text-muted text-uppercase fst-italic fw-bold text-body-tertiary">${course.category}</small>
                  </div>
                </div>
              </label>
            </div>
          `;
                })
                .join("");

            const { value: selectedCourses } = await Swal.fire({
                title: "Select Courses to Add",
                html: `
          <div class="mb-3">
            <input
              type="text"
              id="courseSearch"
              class="form-control"
              placeholder="Search courses..."
            >
          </div>
          <div id="courseList" style="max-height: 400px; overflow-y: auto;">
            ${coursesHtml}
          </div>
          <div class="mt-3 p-2 bg-light rounded">
            <small class="text-muted">
              Current Units: ${this.totalUnits} |
              Min Units: ${this.programDetails.minUnits} |
              Max Units: ${this.programDetails.maxUnits}
            </small>
          </div>
        `,
                width: "600px",
                showCancelButton: true,
                confirmButtonText: "Add Selected Courses",
                cancelButtonText: "Cancel",
                didOpen: () => {
                    // Add search functionality
                    const searchInput = document.getElementById("courseSearch");
                    const courseList = document.getElementById("courseList");

                    searchInput.addEventListener("input", (e) => {
                        const searchTerm = e.target.value.toLowerCase();
                        const courseItems = courseList.querySelectorAll(".form-check");

                        courseItems.forEach(item => {
                            const text = item.textContent.toLowerCase();
                            item.style.display = text.includes(searchTerm) ? "block" : "none";
                        });
                    });
                },
                preConfirm: () => {
                    const checkedBoxes = document.querySelectorAll('input[type="checkbox"]:checked');
                    const selectedIds = Array.from(checkedBoxes).map(cb => cb.value);

                    if (selectedIds.length === 0) {
                        Swal.showValidationMessage("Please select at least one course");
                        return false;
                    }

                    return selectedIds;
                },
            });

            if (selectedCourses && selectedCourses.length > 0) {
                await this.addSelectedCourses(selectedCourses);
            }
        },

        async addSelectedCourses(courseIds) {
            try {
                const requestData = {
                    courseIds: courseIds,
                };

                if (this.selectedSemester) {
                    requestData.year = this.selectedSemester.year;
                    requestData.semester = this.selectedSemester.semester;
                }

                const response = await axios.post(
                    `${process.env.VUE_APP_API_BASE_URL}/course-registration/add-courses`,
                    requestData
                );

                if (response.data.success) {
                    Swal.fire({
                        icon: "success",
                        title: "Courses Added Successfully",
                        text: `${courseIds.length} course(s) have been added to your registration.`,
                    });

                    // Reload the course registration
                    await this.loadCourseRegistration(
                        this.selectedSemester?.year,
                        this.selectedSemester?.semester
                    );
                }
            } catch (error) {
                this.logger.error("Error adding courses:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error Adding Courses",
                    text: error.response?.data?.message || "Failed to add courses. Please try again.",
                });
            }
        },

        async removeCourse(course) {
            const result = await Swal.fire({
                title: "Remove Course?",
                text: `Are you sure you want to remove "${course.code} - ${course.title}" from your registration?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, Remove",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                try {
                    const requestData = {};
                    if (this.selectedSemester) {
                        requestData.year = this.selectedSemester.year;
                        requestData.semester = this.selectedSemester.semester;
                    }

                    // Extract the course ID - handle both populated and non-populated cases
                    const courseId = typeof course.course === 'object' ? course.course._id : course.course;

                    const response = await axios.delete(
                        `${process.env.VUE_APP_API_BASE_URL}/course-registration/remove-course/${courseId}`,
                        { data: requestData }
                    );

                    if (response.data.success) {
                        Swal.fire({
                            icon: "success",
                            title: "Course Removed",
                            text: "The course has been removed from your registration.",
                            timer: 2000,
                            showConfirmButton: false,
                        });

                        // Reload the course registration
                        await this.loadCourseRegistration(
                            this.selectedSemester?.year,
                            this.selectedSemester?.semester
                        );
                    }
                } catch (error) {
                    this.logger.error("Error removing course:", error);
                    Swal.fire({
                        icon: "error",
                        title: "Error Removing Course",
                        text: error.response?.data?.message || "Failed to remove course. Please try again.",
                    });
                }
            }
        },

        async submitRegistration() {
            // Validate minimum units
            if (this.totalUnits < this.programDetails.minUnits) {
                Swal.fire({
                    icon: "warning",
                    title: "Minimum Units Not Met",
                    text: `You need at least ${this.programDetails.minUnits} units to submit. Current: ${this.totalUnits} units.`,
                });
                return;
            }

            const result = await Swal.fire({
                title: "Submit Course Registration?",
                html: `
          <p>You are about to submit your course registration with:</p>
          <ul class="list-unstyled">
            <li><strong>Total Courses:</strong> ${this.courses.length}</li>
            <li><strong>Total Units:</strong> ${this.totalUnits}</li>
            <li><strong>Academic Session:</strong> ${this.currentSession?.sessionYear}</li>
          </ul>
          <p class="text-warning"><strong>Note:</strong> Once submitted, you cannot modify your registration.</p>
        `,
                icon: "question",
                showCancelButton: true,
                confirmButtonColor: "#28a745",
                cancelButtonColor: "#6c757d",
                confirmButtonText: "Yes, Submit",
                cancelButtonText: "Cancel",
            });

            if (result.isConfirmed) {
                try {
                    const requestData = {};
                    if (this.selectedSemester) {
                        requestData.year = this.selectedSemester.year;
                        requestData.semester = this.selectedSemester.semester;
                    }

                    const response = await axios.post(
                        `${process.env.VUE_APP_API_BASE_URL}/course-registration/submit`,
                        requestData
                    );

                    if (response.data.success) {
                        Swal.fire({
                            icon: "success",
                            title: "Registration Submitted Successfully",
                            text: "Your course registration has been submitted for approval.",
                        });

                        // Reload the course registration
                        await this.loadCourseRegistration(
                            this.selectedSemester?.year,
                            this.selectedSemester?.semester
                        );
                    }
                } catch (error) {
                    this.logger.error("Error submitting registration:", error);
                    Swal.fire({
                        icon: "error",
                        title: "Submission Failed",
                        text: error.response?.data?.message || "Failed to submit registration. Please try again.",
                    });
                }
            }
        },

        async generateCoursePDF() {
            try {
                // Create new PDF document
                const doc = new jsPDF({
                    compress: true // Enable PDF compression
                });
                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                // Colors matching the letterhead
                const primaryBlue = [0, 51, 153]; // Deep blue
                // const white = [255, 255, 255]; // White
                const secondaryGray = [146, 146, 146]; // Gray
                // const accentGold = [218, 165, 32]; // Gold

                // ===== LOAD AND OPTIMIZE LOGO ONCE =====
                let logoImg = null;
                try {
                    logoImg = require('@/assets/logo.png');
                } catch (error) {
                    this.logger.warn("Logo not found, continuing without it");
                }

                // ===== WATERMARK =====
                // Add watermark logo in the center of the page with low opacity
                if (logoImg) {
                    try {
                        // Calculate center position for watermark
                        const watermarkWidth = 120; // Large watermark
                        const watermarkHeight = 80;
                        const centerX = (pageWidth - watermarkWidth) / 2;
                        const centerY = (pageHeight - watermarkHeight) / 2;

                        // Add watermark with reduced opacity (0.1 = 10% opacity)
                        doc.saveGraphicsState();
                        doc.setGState(new doc.GState({ opacity: 0.1 }));
                        doc.addImage(logoImg, 'PNG', centerX, centerY, watermarkWidth, watermarkHeight, 'logo', 'FAST');
                        doc.restoreGraphicsState();
                    } catch (error) {
                        this.logger.warn("Could not add watermark logo");
                    }
                }

                // ===== HEADER/LETTERHEAD =====
                let yPos = 20;

                // Add logo on the left side (smaller, for letterhead) - reuse the same image reference
                if (logoImg) {
                    try {
                        doc.addImage(logoImg, 'PNG', 15, 13, 30, 20, 'logo', 'FAST');
                    } catch (error) {
                        this.logger.warn("Could not add letterhead logo");
                    }
                }

                // Institution Name and Details on the right side
                // const textStartX = 50; // Start after logo
                doc.setFontSize(20);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(...primaryBlue);
                doc.text("FIRST CITY POLYTECHNIC",  pageWidth / 2, yPos, { align: "center" });

                yPos += 6;
                doc.setFontSize(9);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(...secondaryGray);
                const fcpAddress = process.env.VUE_APP_FCP_ADDRESS || "";
                doc.text(fcpAddress, pageWidth / 2, yPos, { align: "center" });

                yPos += 5;
                const fcpPhone = process.env.VUE_APP_FCP_PHONE_NO || "";
                const fcpInfoEmail = process.env.VUE_APP_FCP_INFO_EMAIL || "";
                doc.text(`Tel: ${fcpPhone} | Email: ${fcpInfoEmail}`,   pageWidth / 2, yPos, { align: "center" });

                // ===== DOCUMENT TITLE =====
                yPos += 15;
                doc.setFontSize(12);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(0, 0, 0);
                doc.text("COURSE REGISTRATION FORM", pageWidth / 2, yPos, { align: "center" });

                // ===== STUDENT INFORMATION =====
                yPos += 10;
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(0, 0, 0);

                const leftMargin = 15;
                // const rightMargin = pageWidth - 15;
                const midPoint = pageWidth / 2;

                // Get student full name
                const studentName = `${this.currentUser.firstname || ''} ${this.currentUser.othername || ''} ${this.currentUser.lastname || ''}`.trim().toUpperCase();

                // Get program name (from courseRegistration if populated)
                let programName = "N/A";
                if (this.currentUser.program) {
                    if (typeof this.currentUser.program === 'object' && this.currentUser.program.program) {
                        programName = this.currentUser.program.program.name || "N/A";
                    }
                }

                // Left column
                doc.setFont("helvetica", "bold");
                doc.text("Student Name:", leftMargin, yPos);
                doc.setFont("helvetica", "normal");
                doc.text(studentName, leftMargin + 35, yPos);

                // Right column
                doc.setFont("helvetica", "bold");
                doc.text("Matric Number:", midPoint + 10, yPos);
                doc.setFont("helvetica", "normal");
                doc.text(this.currentUser.matricNumber || "N/A", midPoint + 45, yPos);

                yPos += 6;

                // Program
                doc.setFont("helvetica", "bold");
                doc.text("Programme:", leftMargin, yPos);
                doc.setFont("helvetica", "normal");
                doc.text(programName.toUpperCase(), leftMargin + 35, yPos);

                yPos += 6;

                // Year and Semester
                doc.setFont("helvetica", "bold");
                doc.text("Academic Level:", leftMargin, yPos);
                doc.setFont("helvetica", "normal");
                doc.text(`Year ${this.selectedSemester.year}, Semester ${this.selectedSemester.semester}`, leftMargin + 35, yPos);

                // Session
                doc.setFont("helvetica", "bold");
                doc.text("Academic Session:", midPoint + 10, yPos);
                doc.setFont("helvetica", "normal");
                doc.text(this.currentSession?.sessionYear || "N/A", midPoint + 45, yPos);

                yPos += 6;

                // Registration Date and Status
                const regDate = this.courseRegistration.submittedAt
                    ? new Date(this.courseRegistration.submittedAt).toLocaleDateString('en-GB')
                    : new Date().toLocaleDateString('en-GB');

                doc.setFont("helvetica", "bold");
                doc.text("Registration Date:", leftMargin, yPos);
                doc.setFont("helvetica", "normal");
                doc.text(regDate, leftMargin + 35, yPos);

                doc.setFont("helvetica", "bold");
                doc.text("Status:", midPoint + 10, yPos);
                doc.setFont("helvetica", "normal");
                doc.text(this.statusText.toUpperCase(), midPoint + 45, yPos);

                // ===== COURSES TABLE =====
                yPos += 8;

                // Prepare table data
                const tableData = this.courses.map((course, index) => [
                    index + 1,
                    course.code?.toUpperCase() || "N/A",
                    course.title || "N/A",
                    course.unit || 0,
                    course.category?.toUpperCase() || "N/A"
                ]);

                // Add table
                doc.autoTable({
                    startY: yPos,
                    head: [["S/N", "Course Code", "Course Title", "Units", "Category"]],
                    body: tableData,
                    theme: "grid",
                    headStyles: {
                        fillColor: primaryBlue,
                        textColor: [255, 255, 255],
                        fontStyle: "bold",
                        halign: "center"
                    },
                    styles: {
                        fontSize: 9,
                        cellPadding: 2,
                        fillColor: false,
                    },
                    columnStyles: {
                        0: { halign: "center", cellWidth: 15 },
                        1: { halign: "center", cellWidth: 30 },
                        2: { halign: "center", cellWidth: 80 },
                        3: { halign: "center", cellWidth: 20 },
                        4: { halign: "center", cellWidth: 30 }
                    },
                    margin: { left: leftMargin, right: leftMargin },
                    // alternateRowStyles: {
                    //     fillColor: [245, 245, 245]
                    // }
                });

                // Get the Y position after the table
                yPos = doc.lastAutoTable.finalY + 10;

                // ===== SUMMARY =====
                doc.setFontSize(11);
                doc.setFont("helvetica", "bold");
                doc.setTextColor(0, 0, 0);
                doc.text("SUMMARY", leftMargin, yPos);

                yPos += 7;
                doc.setFontSize(10);
                doc.setFont("helvetica", "normal");
                doc.setTextColor(0, 0, 0);

                doc.text(`Total Number of Courses: ${this.courses.length}`, leftMargin, yPos);
                doc.text(`Minimum Units Required: ${this.programDetails.minUnits}`, midPoint + 10, yPos);
                yPos += 6;

                doc.text(`Total Credit Units: ${this.totalUnits}`, leftMargin, yPos);
                doc.text(`Maximum Units Allowed: ${this.programDetails.maxUnits}`, midPoint + 10, yPos);

                // ===== SIGNATURE SECTION =====
                yPos += 20;

                // Check if we need a new page for signatures
                if (yPos > pageHeight - 60) {
                    doc.addPage();
                    yPos = 20;
                }

                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.3);

                // Student Signature
                doc.line(leftMargin, yPos, leftMargin + 60, yPos);
                yPos += 5;
                doc.setFont("helvetica", "bold");
                doc.text("Student's Signature & Date", leftMargin, yPos);

                // Course Advisor/HOD Signature
                const rightSigStart = pageWidth - 75;
                yPos -= 5;
                doc.line(rightSigStart, yPos, rightSigStart + 60, yPos);
                yPos += 5;
                doc.text("Course Advisor's Signature & Date", rightSigStart, yPos);

                yPos += 15;

                // Department Stamp
                doc.line(leftMargin, yPos, leftMargin + 60, yPos);
                yPos += 5;
                doc.text("Head of Department's Signature & Date", leftMargin, yPos);

                // Departmental Stamp box
                yPos -= 5;
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.2);
                doc.rect(rightSigStart, yPos - 13, 60, 20);
                yPos += 5;
                doc.setFont("helvetica", "italic");
                doc.setFontSize(9);
                doc.setTextColor(...secondaryGray);
                doc.text("(Departmental Stamp)", rightSigStart + 30, yPos, { align: "center" });

                // ===== FOOTER NOTE =====
                yPos += 15;
                doc.setFontSize(8);
                doc.setFont("helvetica", "italic");
                doc.setTextColor(...secondaryGray);
                doc.text(
                    "Note: This form must be duly signed and stamped before submission to the Registry.",
                    pageWidth / 2,
                    yPos,
                    { align: "center" }
                );

                // ===== FOOTER WITH DECORATIVE ELEMENTS =====
                const footerY = pageHeight - 15;

                // Decorative triangles at bottom left (matching letterhead style)

                // Gray triangle overlay
                // doc.setFillColor(...secondaryGray);
                // doc.triangle(0, pageHeight - 38, 0, pageHeight, 38, pageHeight, 'F');

                // Gold triangle rectangle
                // doc.setFillColor(...accentGold);
                // const pWidth = 26;
                // const pHeight = 19;
                // const pSlant = 20;

                // Start exactly at bottom-left
                // const pStartX = 0;
                // const pStartY = pageHeight - pHeight;
                // doc.lines(
                //   [
                //     [pWidth, 0],        // top edge (to the right)
                //     [pSlant, pHeight],  // right slanted down
                //     [-pWidth, 0],      // bottom edge (back left)
                //     [0, 0]      // left edge straight up (back to start)
                //   ],
                //   pStartX,
                //   pStartY,
                //   [1, 1],
                //   "F"
                // );

                // White triangle
                // doc.setFillColor(...white);
                // doc.triangle(0, pageHeight - 31, 0, pageHeight, 31, pageHeight, 'F');

                // Blue triangle overlay
                // doc.setFillColor(...primaryBlue);
                // doc.triangle(0, pageHeight - 25, 0, pageHeight, 25, pageHeight, 'F');

                // Footer text
                doc.setFontSize(8);
                doc.setTextColor(...secondaryGray);
                doc.text(
                    `Generated on: ${new Date().toLocaleString('en-GB')}`,
                    pageWidth / 2,
                    footerY - 3,
                    { align: "center" }
                );

                // Save the PDF
                const fileName = `Course_Registration_${this.currentUser.matricNumber || this.currentUser.applicationNumber}_${this.selectedSemester.year}_${this.selectedSemester.semester}.pdf`;
                doc.save(fileName);

                Swal.fire({
                    icon: "success",
                    title: "PDF Generated Successfully",
                    text: "Your course registration form has been downloaded.",
                    timer: 2000,
                    showConfirmButton: false
                });

            } catch (error) {
                this.logger.error("Error generating PDF:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error Generating PDF",
                    text: "Failed to generate the course registration form. Please try again."
                });
            }
        },
    },
};
</script>

<template>
  <div class="card border-0 shadow-sm" :style="{ height: height }">
    <div
      class="card-header bg-dark text-white d-flex justify-content-between align-items-center"
    >
      <div class="d-flex flex-column w-100">
        <!-- Semester Selection -->
        <div class="d-flex align-items-center mb-2" v-if="!dataLoading">
          <select
            v-model="selectedSemester"
            @change="onSemesterChange"
            class="form-select form-select-sm me-3"
            style="max-width: 180px"
          >
            <option :value="null" disabled>Select Semester</option>
            <option
              v-for="semester in yearSemesters"
              :key="`${semester.value.year}-${semester.value.semester}`"
              :value="semester.value"
            >
              {{ semester.text }}
            </option>
          </select>

          <!-- Status Badge -->
          <span :class="['badge', statusBadgeClass]">
            {{ statusText }}
          </span>
        </div>

        <!-- Loading placeholder for semester -->
        <div v-else-if="enabled && dataLoading" class="mb-2">
          <span class="placeholder col-6 me-3"></span>
          <span class="placeholder col-3"></span>
        </div>

        <span class="small fw-bold">Courses</span>
      </div>

      <div class="d-flex">
        <!-- Show Add and Submit buttons when in draft/not registered status -->
        <template v-if="showActionButtons">
          <button
            @click="addCourse"
            class="btn btn-light btn-sm me-2 py-0"
            :disabled="
              !enabled ||
              !isDraftOrNotRegistered ||
              dataLoading ||
              hasReachedMaxUnits ||
              !isCurrentSemester
            "
          >
            <span
              v-if="dataLoading"
              class="spinner-border spinner-border-sm me-1"
            ></span>
            Add
          </button>
          <button
            @click="submitRegistration"
            class="btn btn-outline-light btn-sm me-2 py-0"
            :disabled="
              !enabled ||
              !canSubmit ||
              !isDraftOrNotRegistered ||
              dataLoading ||
              !isCurrentSemester
            "
          >
            Submit
          </button>
        </template>

        <!-- Show Print button when submitted/approved -->
        <button
          v-if="canPrint"
          @click="generateCoursePDF"
          class="btn btn-success btn-sm py-0 d-flex"
          :disabled="dataLoading"
          title="Print Course Registration Form"
        >
          <i class="bi bi-printer me-1"></i>
          Print
        </button>
      </div>
    </div>

    <div class="card-body overflow-auto">
      <!-- Alert for non-admitted students (no matric number or not active) -->
      <div
        v-if="!isAdmittedStudent && !loading"
        class="alert alert-warning d-flex align-items-center mb-3"
        role="alert"
      >
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <div>
          <strong>Admission Required</strong>
          <p class="mb-0 mt-1">
            Course registration is only available to admitted students.
            <span v-if="!currentUser.matricNumber">
              You have not been assigned a matric number yet.
            </span>
            <span v-else-if="currentUser.status !== 'active'">
              Your student status is "{{ currentUser.status }}". Only students
              with "active" status can register courses.
            </span>
            Please contact the admissions office for more information.
          </p>
        </div>
      </div>

      <!-- Alert when viewing a different semester than current -->
      <div
        v-if="
          !isCurrentSemester &&
          selectedSemester &&
          currentUser &&
          isAdmittedStudent
        "
        class="alert alert-info d-flex align-items-center mb-3 small"
        role="alert"
      >
        <i class="bi bi-info-circle-fill me-2"></i>
        <div>
          <strong>Viewing past/future semester.</strong>
          You can only register courses for your current semester.
        </div>
      </div>

      <!-- Alert when registration is closed but student has started -->
      <div
        v-if="!enabled && hasStartedRegistration && isAdmittedStudent"
        class="alert alert-warning d-flex align-items-center mb-3 small"
        role="alert"
      >
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <div>
          <strong>Course registration is currently closed.</strong>
          <span v-if="courseRegistration.status === 'draft'">
            You can view your courses but cannot make changes until registration
            reopens.
          </span>
          <span v-else-if="courseRegistration.status === 'submitted'">
            Your registration has been submitted and is pending approval.
          </span>
          <span v-else-if="courseRegistration.status === 'approved'">
            Your registration has been approved.
          </span>
          <span v-else-if="courseRegistration.status === 'rejected'">
            Your registration was rejected. Please contact your department when
            registration reopens.
          </span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="dataLoading" class="text-center py-4">
        <div class="spinner-border text-primary mb-3" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-muted">Loading course registration...</p>
      </div>

      <!-- Course List -->
      <div v-else-if="courses.length > 0">
        <div v-for="course in courses" :key="course.course" class="card mb-3">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-start">
              <div class="flex-grow-1">
                <h6 class="card-title mb-1 fw-bold">
                  Code:
                  <span class="text-uppercase">{{ course.code }}</span>
                </h6>
                <p class="card-text mb-2">Title: {{ course.title }}</p>
                <div class="d-flex align-items-center">
                  <span class="badge bg-secondary me-2"
                    >{{ course.unit }} Units</span
                  >
                  <small class="text-muted text-capitalize">{{
                    course.category
                  }}</small>
                </div>
              </div>

              <div class="ms-3">
                <button
                  v-if="showActionButtons"
                  @click="removeCourse(course)"
                  class="btn btn-outline-danger btn-sm"
                  :disabled="!enabled || dataLoading"
                  title="Remove course"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>

            <!-- Course added date -->
            <!-- <small class="text-muted d-block mt-2">
              Added: {{ new Date(course.date_added).toLocaleDateString() }}
            </small> -->
          </div>
        </div>
      </div>

      <!-- Empty State - Registration Open (Only for admitted students) -->
      <div
        v-else-if="
          enabled && !dataLoading && isAdmittedStudent && isCurrentSemester
        "
        class="text-center py-4"
      >
        <i class="bi bi-journal-plus text-muted" style="font-size: 3rem"></i>
        <h5 class="text-muted mt-3">No Courses Registered</h5>
        <p class="text-muted">
          Start by selecting your semester and adding courses.
        </p>
        <button
          @click="addCourse"
          class="btn btn-primary btn-sm"
          :disabled="
            !selectedSemester || !isDraftOrNotRegistered || !isCurrentSemester
          "
        >
          <i class="bi bi-plus-circle me-1"></i>
          Add Your First Course
        </button>
      </div>

      <!-- Empty State - Not Admitted -->
      <div
        v-else-if="!isAdmittedStudent && !dataLoading"
        class="text-center py-5"
      >
        <i class="bi bi-person-x text-warning" style="font-size: 3rem"></i>
        <h5 class="text-muted mt-3">Admission Required</h5>
        <p class="text-muted px-3">
          Course registration is only available to admitted students with active
          status.
        </p>
      </div>

      <!-- Empty State - Registration Closed -->
      <div
        v-else-if="!enabled && !dataLoading && isAdmittedStudent"
        class="text-center py-5"
      >
        <i class="bi bi-lock text-muted" style="font-size: 3rem"></i>
        <h5 class="text-muted mt-3">Try again later</h5>
        <p class="text-muted px-3">
          {{ statusReason }}
        </p>
        <p class="text-muted small" v-if="!requiresAdmission">
          You have no courses registered for this semester yet.<br />
          Select a different semester to view your other registrations.
        </p>
      </div>
    </div>

    <!-- Footer with totals and progress -->
    <div class="card-footer bg-light border-top">
      <div class="row align-items-center">
        <div class="col-md-6">
          <div v-if="!dataLoading && courseRegistration">
            <span class="fw-bold text-dark me-2">Total Units:</span>
            <span class="fs-6">
              {{ totalUnits }} Unit<span v-if="totalUnits > 1">s</span></span
            >
            <!-- <span class="badge bg-primary fs-6">{{ totalUnits }}</span> -->
          </div>
          <div v-else>
            <span class="placeholder col-6"></span>
          </div>
        </div>

        <div class="col-md-6" v-if="!dataLoading && courseRegistration">
          <div class="text-end">
            <small class="text-muted">
              Min: {{ programDetails.minUnits }} | Max:
              {{ programDetails.maxUnits }}
            </small>
            <div class="progress mt-1" style="height: 6px">
              <div
                class="progress-bar"
                :class="{
                  'bg-warning': totalUnits < programDetails.minUnits,
                  'bg-success':
                    totalUnits >= programDetails.minUnits &&
                    totalUnits <= programDetails.maxUnits,
                  'bg-danger': totalUnits > programDetails.maxUnits,
                }"
                :style="{
                  width: `${Math.min(
                    (totalUnits / programDetails.maxUnits) * 100,
                    100
                  )}%`,
                }"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Initial loading overlay - only shown during first load -->
    <div
      v-if="loading"
      class="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-dark bg-opacity-75 rounded"
      style="z-index: 10"
    >
      <div class="text-center">
        <div class="spinner-border text-white mb-3" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <h5 class="text-white fw-bold">Checking status...</h5>
      </div>
    </div>

    <div
      v-if="!isAdmittedStudent && !loading"
      class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-3"
      style="z-index: 10; cursor: not-allowed"
    >
      <h5 class="text-white fw-bold">Not activated yet</h5>
    </div>
  </div>
</template>

<style scoped></style>
